import { Modal, PDFDocumentView, notification } from "components/common"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"

import parentApi from "store/parentApi"
import { useLazyGetPolicyQuery, useLazyGetViewPolicyAnnouncementQuery } from "features/policies/api"
import { useSelector } from "react-redux"
import { t } from "i18next"
import { LEADS_LISTING_PATH } from "constants/app-paths"
import { getLanguagePrefix, localisedValue, sanitizeHTML } from "utils"
import { useSearchParams } from "react-router-dom"

const okButtonLabels = () => ({
  repository_policy: t("View Policy"),
  lead: t("View Lead"),
})

const toAnnouncementMessageHtml = announcement =>
  sanitizeHTML(localisedValue({ object: announcement, key: "message" }))

const AnnouncementModal = forwardRef((_props, ref) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const user = useSelector(state => state?.app?.user?.info)
  const [acknowledge, { isLoading: isLoadingAck }] = parentApi.useUpdatePendingAnnouncementsMutation()
  const [getViewPolicy, { data: policy, isLoading, error }] = useLazyGetViewPolicyAnnouncementQuery()
  const [announcements, setAnnouncements] = useState()
  const [announcement, setAnnouncement] = useState()

  const modalRef = useRef()
  const confirmModalRef = useRef()
  const policyModalRef = useRef()
  const isFromAnnouncement = searchParams.has("fromAnnouncement")

  useImperativeHandle(
    ref,
    () => ({
      showFor(data) {
        if (isFromAnnouncement) return
        setAnnouncements(data)
        setAnnouncement(data[0])
        modalRef.current.setVisible(true)
      },
    }),
    []
  )

  const setNewAnnouncement = () => {
    setAnnouncement(prev => {
      const pendingAnnouncements = announcements?.filter(e => e?.announcementId !== prev?.announcementId)
      setAnnouncements([...pendingAnnouncements])
      if (!pendingAnnouncements?.length) modalRef?.current?.setVisible(false)
      return pendingAnnouncements?.[0]
    })
  }

  const onConfirm = () => {
    setNewAnnouncement()
    confirmModalRef?.current?.setVisible(false)
    policyModalRef.current.setVisible(false)
  }

  const onViewPolicy = () => {
    getViewPolicy(announcement?.againstId).then(e => {
      if (e?.error) {
        notification.error({ message: e?.error?.message?.message })
      } else {
        policyModalRef.current.setVisible(true)
      }
    })
  }

  const onViewLead = () => {
    window.open(
      `${getLanguagePrefix()}${LEADS_LISTING_PATH}?id=${announcement?.againstId}&fromAnnouncement=true`,
      "_blank",
      "noopener,noreferrer"
    )
    onAcknowledge()
  }

  const onAcknowledge = () => {
    if (announcement?.announcementId) {
      acknowledge({ userId: user?.id, announcementId: announcement?.announcementId }).then(e => {
        if (!e.error) {
          onConfirm()
        } else {
          notification.error({ message: e.error?.message?.message || e.error?.message })
        }
      })
    }
  }

  const onOk = () => {
    switch (announcement?.againstTypeKey) {
      case "repository_policy":
        onViewPolicy()
        return
      case "lead":
        onViewLead()
        return
      default:
        confirmModalRef?.current?.setVisible(true)
        return
    }
  }

  return (
    <>
      <Modal
        width={640}
        ref={modalRef}
        onCancel={() => confirmModalRef?.current?.setVisible(true)}
        hideCancelButton
        bodyStyle={{ padding: 24 }}
        destroyOnClose
        title={localisedValue({ object: announcement, key: "title" })}
        subTitle={announcement?.from ? `${t("By")} ${localisedValue({ object: announcement, key: "from" })}` : ""}
        loading={isLoading}
        okText={okButtonLabels()[announcement?.againstTypeKey] || t("Ok")}
        onOk={onOk}
      >
        {announcement?.message && (
          <div className="gray-900" dangerouslySetInnerHTML={{ __html: toAnnouncementMessageHtml(announcement) }} />
        )}
      </Modal>

      <Modal
        ref={policyModalRef}
        onCancel={() => confirmModalRef?.current?.setVisible(true)}
        hideCancelButton
        bodyStyle={{ padding: 24 }}
        destroyOnClose
        title="Policy"
        footer={null}
      >
        {policy?.Attachment?.path && (
          <PDFDocumentView isLoading={isLoading} error={error} file={policy?.Attachment?.path} />
        )}
      </Modal>

      <Modal
        width={400}
        ref={confirmModalRef}
        onCancel={() => confirmModalRef?.current?.setVisible(false)}
        bodyStyle={{ padding: 24 }}
        onOk={onAcknowledge}
        okText="Yes"
        destroyOnClose
        title="Confirmation"
        loading={isLoadingAck}
      >
        {t("Are you sure that you have read the message?")}
      </Modal>
    </>
  )
})

export default AnnouncementModal
